import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useLocation } from 'wouter';

interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  title?: string;
  description?: string;
  location?: string;
  category: string;
  tags: string[];
  projectId?: string;
  isPrimary?: boolean;
}

interface GallerySectionProps {
  onImageClick?: (index: number, image: any) => void;
  onContactClick?: () => void;
}

// Quick-view lightbox for homepage
const QuickLightbox = ({ isOpen, onClose, image }: { isOpen: boolean; onClose: () => void; image: GalleryImage | null }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !image) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/95 z-50 flex flex-col"
      onClick={onClose}
    >
      {/* Close bar */}
      <div className="flex justify-end px-4 py-3 flex-shrink-0">
        <button
          onClick={onClose}
          className="flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium bg-white/10 active:bg-white/20 rounded-full px-4 py-2.5 border border-white/20"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Close
        </button>
      </div>

      <div
        className="flex-1 flex items-center justify-center px-3 pb-4 min-h-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative w-full max-w-4xl max-h-full flex flex-col">
          <div className="relative flex-shrink min-h-0">
            <img
              src={`/api/gallery/${image.id}/image`}
              alt={image.alt}
              className="w-full h-full max-h-[70vh] object-contain rounded-xl shadow-2xl"
            />
          </div>
          <div className="mt-4 px-1">
            <h3 className="text-white text-base sm:text-xl font-bold">{image.title || image.alt}</h3>
            {image.description && <p className="text-gray-400 mt-1 text-sm line-clamp-2">{image.description}</p>}
            {image.location && <p className="text-gray-500 text-xs mt-1">📍 {image.location}</p>}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default function GallerySection({ onImageClick, onContactClick }: GallerySectionProps = {}) {
  const [, navigate] = useLocation();
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);

  const openLightbox = (image: GalleryImage) => {
    setSelectedImage(image);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    setLightboxOpen(false);
  };

  useEffect(() => {
    const fetchGalleryImages = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch('/api/gallery');
        if (!response.ok) throw new Error('Failed to fetch gallery images');
        const data = await response.json();
        if (data.success) setGalleryImages(data.data || []);
        else throw new Error(data.message || 'Failed to load gallery images');
      } catch {
        setError('Failed to load gallery images.');
        setGalleryImages([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchGalleryImages();
  }, []);

  const getFeaturedImages = () => {
    if (!galleryImages.length) return [];
    const sorted = [...galleryImages].sort((a, b) => b.id - a.id);
    const grouped = sorted.reduce((acc, image) => {
      const key = image.projectId || `single-${image.id}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(image);
      return acc;
    }, {} as Record<string, GalleryImage[]>);
    return Object.keys(grouped)
      .map(key => grouped[key].find(img => img.isPrimary) || grouped[key][0])
      .slice(0, 6);
  };

  const featuredImages = getFeaturedImages();

  const PLACEHOLDER = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMDAgMTUwTDE1MCAyMDBIMjUwTDIwMCAxNTBaIiBmaWxsPSIjRDFEOUUwIi8+CjxjaXJjbGUgY3g9IjE3MCIgY3k9IjEyMCIgcj0iMjAiIGZpbGw9IiNEMUQ5RTAiLz4KPHRleHQgeD0iMjAwIiB5PSIyNjAiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY3NzI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pgo8L3N2Zz4K';

  if (isLoading) {
    return (
      <section id="gallery" className="py-14 sm:py-20 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">Our Work Gallery</h2>
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full inline-block mt-8" />
        </div>
      </section>
    );
  }

  if (!featuredImages.length) {
    return (
      <section id="gallery" className="py-14 sm:py-20 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">Our Work Gallery</h2>
          <p className="text-gray-500 mt-8">No gallery images available at the moment.</p>
        </div>
      </section>
    );
  }

  return (
    <section id="gallery" className="py-14 sm:py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">Our Work Gallery</h2>
          <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto px-2">
            Explore our portfolio of exceptional welding projects
          </p>
        </div>

        {/* Grid — 2 cols on mobile, 3 on desktop */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mb-10 sm:mb-12">
          {featuredImages.map((image, index) => (
            <motion.div
              key={image.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              viewport={{ once: true }}
              className="group relative aspect-[4/3] rounded-xl sm:rounded-2xl overflow-hidden bg-gray-200 shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer active:scale-[0.98] transition-transform"
              onClick={() => openLightbox(image)}
            >
              <img
                src={`/api/gallery/${image.id}/image`}
                alt={image.alt}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
                onError={(e) => {
                  const t = e.currentTarget;
                  if (!t.dataset.retry) {
                    t.dataset.retry = 'true';
                    const f = image.src?.split('/').pop();
                    t.src = f ? `/uploads/${f}` : PLACEHOLDER;
                  } else {
                    t.src = PLACEHOLDER;
                  }
                }}
              />

              {/* Always-visible bottom gradient — shows title on mobile */}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent pt-6 pb-2.5 px-2.5 sm:pt-8 sm:pb-4 sm:px-4">
                <h3 className="text-white font-semibold text-xs sm:text-sm leading-tight line-clamp-1">
                  {image.title || 'Welding Project'}
                </h3>
                <p className="text-gray-400 text-[10px] sm:text-xs mt-0.5 capitalize">
                  {image.category.replace(/-/g, ' ')}
                </p>
              </div>

              {/* Desktop hover buttons */}
              <div className="hidden sm:flex absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-all duration-300 items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <Button
                  variant="secondary"
                  size="sm"
                  className="bg-white/90 text-black hover:bg-white text-xs"
                  onClick={(e) => { e.stopPropagation(); openLightbox(image); }}
                >
                  Quick View
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="bg-white/90 text-black hover:bg-white text-xs"
                  onClick={(e) => { e.stopPropagation(); navigate(`/gallery?image=${image.id}`); }}
                >
                  Details
                </Button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA — full width on mobile */}
        <div className="text-center">
          <Button
            size="lg"
            onClick={() => navigate('/gallery')}
            className="w-full sm:w-auto bg-black/80 hover:bg-black text-white px-8 py-3 text-sm sm:text-base active:bg-black rounded-full"
          >
            View Full Gallery
          </Button>
        </div>
      </div>

      {/* Quick-view lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <QuickLightbox isOpen={lightboxOpen} onClose={closeLightbox} image={selectedImage} />
        )}
      </AnimatePresence>
    </section>
  );
}
